import React, { useState, useEffect, useCallback, useRef } from 'react';
import logo from '../resources/BSG_Traders.png';
import { getMarketStatus, getAllIndicies, getAnalysis, getGammaAnalysis, getEmaData, getExpiryDates, triggerAutoStart, sendTelegramNotification, getLeadingStocks, fetchUserTrades, saveUserTrade, patchUserTrade, clearUserTrades, getKiteAnalysis, getKiteGammaAnalysis, getSnapshot, subscribeToStream, getSystemStatus } from '../services/apiService';
import { setLiveTicks } from '../services/liveTicks';
import * as XLSX from 'xlsx';
import DataTable from '../components/DataTable';
import GammaPanel from '../components/GammaPanel';
import PremiumBehaviourPanel from '../components/PremiumBehaviourPanel';
import VolumeSpikePanel from '../components/VolumeSpikePanel';
import IndicatorPanel from '../components/IndicatorPanel';
import TradingSetupPanel from '../components/TradingSetupPanel';
import TradeHistoryPanel from '../components/TradeHistoryPanel';
import EmaPanel from '../components/EmaPanel';
import GreekStopLossPanel from '../components/GreekStopLossPanel';
import FuturesPanel from '../components/FuturesPanel';
import WeeklyCondorPanel from '../components/WeeklyCondorPanel';
import LevelShortPanel from '../components/LevelShortPanel';
import MenuBar from '../components/MenuBar';
import UserManagementPanel from '../components/UserManagementPanel';
import AdminTradesDashboard from '../components/AdminTradesDashboard';
import PerformancePanel from '../components/PerformancePanel';
import RiskSettingsPanel from '../components/RiskSettingsPanel';
import { roundTripCharges, BROKERAGE_PER_ORDER } from '../utils/tradeCosts';
import '../styles/AnalyzerPage.css';

const SYMBOL_DISPLAY = {
  NIFTY: 'NIFTY 50', BANKNIFTY: 'BANK NIFTY', SENSEX: 'SENSEX (BSE)',
  BANKEX: 'BANKEX (BSE)', MIDCPNIFTY: 'MIDCAP NIFTY', FINNIFTY: 'FIN NIFTY', 'NIFTY IT': 'NIFTY IT',
};
const symName = s => SYMBOL_DISPLAY[s] || s;

// Trades saved before the ZonedDateTime-suffix fix in UserTradeService.nowIso() have
// timestamp/exitTime strings like "2026-08-26T14:23:10.123+05:30[Asia/Kolkata]" — the
// trailing "[Asia/Kolkata]" is Java-only and makes the JS `Date` constructor return
// Invalid Date (renders as "Invalid Date Invalid Date" everywhere it's formatted).
// Strip it here, once, at the point server trades enter frontend state.
const sanitizeTimestamp = ts => (typeof ts === 'string' ? ts.replace(/\[[^\]]+\]$/, '') : ts);

const fmtIST = ts => {
  try {
    return new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
  } catch { return ''; }
};

const TRADE_HISTORY_KEY = 'bsg_trade_history';

// A selling structure's stored legs ({legs:[{side,right,strike,entryPx}]}) in the shape the
// backend's live openStrategy.legDetails uses, so the panel renders either the same way.
const parseLegs = json => {
  try {
    return (JSON.parse(json)?.legs ?? []).map(l => ({
      side: l.side, strike: l.strike, entryPx: l.entryPx,
      right: l.right === 'call' || l.right === 'CE' ? 'CE' : 'PE',
    }));
  } catch { return undefined; }
};

// True if `ts` falls on today's IST calendar date. Used to separate "today" from "all-time"
// counts in the square-off Telegram messages — tradeHistory itself is never date-scoped
// (see generateEODReport's own todayTrades filter below for the report-generation use case),
// so anything summed over it directly is a running total across every day ever recorded,
// not "this session" despite that being the line's old label.
const isToday = ts => {
  try {
    const d = new Date(ts);
    const now = new Date();
    return d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  } catch { return false; }
};

const AnalyzerPage = ({ config, onLogout, onBack, currentUser, onUpdateUser }) => {
  const isViewer = currentUser?.role === 'VIEWER';
  const isAdmin  = currentUser?.role === 'ADMIN';
  const onRiskLots    = currentUser?.onRiskLots    ?? 1;
  const takeTradeLots = currentUser?.takeTradeLots ?? 2;

  const [analysisData, setAnalysisData] = useState([]);
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [sseConnected, setSseConnected] = useState(false);
  const [autoStop, setAutoStop] = useState(true);
  const [liveExport, setLiveExport] = useState(false);
  const [liveRefreshInterval, setLiveRefreshInterval] = useState(config.refreshInterval);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [showUserMgmt, setShowUserMgmt]       = useState(false);
  const [showAdminTrades, setShowAdminTrades] = useState(false);
  const [showPerformance, setShowPerformance] = useState(false);
  const [showRiskSettings, setShowRiskSettings] = useState(false);
  const [leadingStocksData, setLeadingStocksData] = useState(null);
  const [error, setError] = useState(null);
  const [marketStatus, setMarketStatus] = useState(null);
  const [marketSnapshot, setMarketSnapshot] = useState(null);
  const [gammaData, setGammaData] = useState(null);
  const [emaData,   setEmaData]   = useState(null);
  const [backendVerdict, setBackendVerdict] = useState(null);
  // TradingSetupPanel's live finalVerdict/dir, lifted up so a manual trade started in the
  // Greek Stop-Loss panel can tag itself TAKE TRADE / ON RISK the same way a system-detected
  // signal would, instead of always falling back to a bare 'MANUAL' label.
  const [liveVerdict, setLiveVerdict] = useState({ finalVerdict: null, dir: null });
  // This user's own open manual trades on the current symbol, as reported by the backend's
  // ManualTradeTrackingService — pushed personalized (per-username) inside the SSE payload's
  // manualPositions field. GreekStopLossPanel renders this instead of computing its own
  // live SL/milestone state.
  const [manualPositions, setManualPositions] = useState([]);
  // Which clientIds we've seen open so far — lets the SSE handler notice when the backend
  // closes one (auto square-off, or a square-off from another tab) so tradeHistory can be
  // refreshed without waiting for a full page reload.
  const seenManualPositionIdsRef = useRef(new Set());

  const prevVixRef = useRef(null); // tracks previous VIX for direction detection
  const [vixDirection, setVixDirection] = useState(null); // 'declining' | 'rising' | 'flat' | null

  // ── Auto-managed config: expiry rollover + ATM tracking + OI-based S/R ──
  // Using a ref so values are always current inside fetchAnalysis without
  // causing the useCallback/interval to be recreated on every update.
  const activeConfigRef  = useRef({ expiryDate: config.expiryDate, strikePrice: config.strikePrice });
  const [autoSupport,      setAutoSupport]      = useState(null); // put-wall OI strike
  const [autoResistance,   setAutoResistance]   = useState(null); // call-wall OI strike
  const [activeExpiryDate, setActiveExpiryDate] = useState(config.expiryDate); // for header display
  const [availableExpiries, setAvailableExpiries] = useState([]); // fetched expiry list
  const [manualStrike,     setManualStrike]     = useState(null); // null = follow ATM
  const [liveLotSize,      setLiveLotSize]      = useState(null); // null = use symbol default
  const manualExpiryRef = useRef(false); // true when user has manually chosen expiry (suppress auto-rollover)
  // Set when the backend's theta-gate auto-adjustment is tracking a different expiry than
  // what this dashboard was showing — holds the ORIGINAL expiry that got overridden, so the
  // warning banner can say what changed. Null once dismissed or never diverged.
  const [expiryAutoSyncedFrom, setExpiryAutoSyncedFrom] = useState(null);

  // ── Closed-market snapshot ────────────────────────────────────────────────
  // When market closes, one final fetch is done to capture the last moment's data.
  // The timestamp of that snapshot is shown in a banner so the user sees frozen data
  // rather than a blank screen.
  const [closedSnapshotTime, setClosedSnapshotTime] = useState(null); // HH:MM string or null
  const finalFetchDoneRef = useRef(false); // ensures the close-snapshot is fetched exactly once

  // ── Gamma flip crossover detection ───────────────────────────────────────
  const prevGammaSpotRef         = useRef(null);   // previous poll's spot price
  const gammaFlipCrossAgeRef     = useRef(0);       // polls elapsed since last crossover
  const [gammaFlipCrossover, setGammaFlipCrossover] = useState(null); // 'UP' | 'DOWN' | null

  // ── SSE sequencing: prevents stale HTTP responses from overwriting live SSE data ──
  // Once SSE delivers its first event this is true; fetchAnalysis skips analysis/gamma/ema
  // updates (but still fetches marketSnapshot, VIX, leadStocks — which SSE doesn't carry).
  const sseHasDeliveredRef = useRef(false);
  // Stable ref for manualStrike so the SSE handler closure can always read the latest value.
  const manualStrikeRef = useRef(manualStrike);
  useEffect(() => { manualStrikeRef.current = manualStrike; }, [manualStrike]);

  // ── PCR history (last 10 polls) for divergence detection ─────────────────
  const [pcrHistory, setPcrHistory] = useState([]);

  // ── OI matrix history (last 3 polls) for 2-of-3 confirmation ─────────────
  const [oiMatrixHistory, setOiMatrixHistory] = useState([]);

  // ── Session trade tracker (win/loss circuit breaker) ──────────────────────
  // For a non-admin user this is pure local browser-tab state (resets on reload, drives its
  // own independent circuit breaker). For the ADMIN it is instead force-synced below from
  // backendSystemStatus — the admin IS the account the backend's SYSTEM engine trades under,
  // so running an independent local copy here would just drift out of sync with (and could
  // open a duplicate position alongside) what the backend has already decided.
  const [sessionTrades, setSessionTrades] = useState({ wins: 0, losses: 0 });
  // Backend truth for the current symbol's autonomous SYSTEM engine — circuit breaker state,
  // today's win/loss count, and the open position (if any). Only polled/used for ADMIN.
  const [backendSystemStatus, setBackendSystemStatus] = useState(null);
  // Non-admin: lifted straight from TradingSetupPanel's own autoTradeRef via onLiveTradeChange
  // (see TradingSetupPanel.js) — null when no local auto-tracked trade is open.
  const [liveTrade, setLiveTrade] = useState(null);
  // Unified "is there a live trade right now" flag, regardless of source — locks the global
  // expiry/strike dropdowns in MenuBar so changing them mid-trade can't corrupt its LTP lookup.
  const hasLiveTrade = isAdmin ? !!backendSystemStatus?.openPosition : !!liveTrade;

  // ── Trade history — server-side (cross-browser sync) + localStorage fallback
  const [tradeHistory, setTradeHistory] = useState(() => {
    try {
      const stored = localStorage.getItem(`${TRADE_HISTORY_KEY}_${config.symbol}`);
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  // Write-through to localStorage so offline / first-render is instant
  useEffect(() => {
    // Keyed per symbol (the page remounts on every symbol switch) — one shared key showed the
    // last-viewed index's trades on an index that had none.
    try { localStorage.setItem(`${TRADE_HISTORY_KEY}_${config.symbol}`, JSON.stringify(tradeHistory)); }
    catch { /* storage quota — silently ignore */ }
  }, [tradeHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Maps a backend UserTrade entity to the frontend's trade shape.
  // `source` distinguishes autonomous backend-opened positions from human-recorded
  // ones — both live in the same table, scoped by username ("SYSTEM" vs a real login).
  const mapServerTrade = (t, source) => ({
    id:              t.clientId,
    source,
    timestamp:       sanitizeTimestamp(t.timestamp),
    symbol:          t.symbol,
    expiryDate:      t.expiryDate,
    signal:          t.signal,
    verdict:         t.verdict,
    finalVerdict:    t.finalVerdict,
    isManual:        t.isManual,
    dir:             t.dir,
    callScore:       t.callScore,
    putScore:        t.putScore,
    confluenceScore: t.confluenceScore,
    dte:             t.daysToExpiry,
    dteCategory:     t.dteCategory,
    mtfAligned:      t.mtfAligned,
    htfRegime:       t.htfRegime,
    dailyBias:       t.dailyBias,
    srBlocked:       t.srBlocked,
    pcrOpposing:     t.pcrOpposing,
    oiSetupSignal:   t.oiSetupSignal,
    vwapSignal:      t.vwapSignal,
    entryStrike:     t.entryStrike,
    entryLTP:        t.entryLTP,
    exitLTP:         t.exitLTP,
    exitTime:        sanitizeTimestamp(t.exitTime),
    exitReason:      t.exitReason,
    pnlPts:          t.pnlPts,
    outcome:         t.outcome,
    lotSize:         t.lotSize,
    numLots:         t.numLots,
    allocatedLotSize: t.allocatedLotSize,
    slPct:           t.slPct,
    // Range-day selling structure (backend StrategyEngine): entryLTP/exitLTP are the net
    // credit/debit per unit and `signal` holds the compact structure label.
    strategy:        t.strategy,
    structure:       t.strategy ? t.signal : undefined,
    legs:            t.strategy ? parseLegs(t.legsJson) : undefined,
  });

  // Fetches + merges the logged-in user's own (manual) trades with the backend's autonomous
  // SYSTEM-owned trades for this session's symbol, so TradeHistoryPanel (and
  // GreekStopLossPanel's own closed-trade lookup) show both regardless of whether this
  // browser tab was open when the backend opened/closed a position. Server-enforced role
  // scoping — see UserTradeController.getTrades: every role gets only its own trades +
  // SYSTEM's trades for config.symbol here; full cross-symbol, cross-user visibility stays
  // exclusive to the Admin Trades Dashboard.
  const refreshTradeHistory = useCallback(() => {
    if (!currentUser?.username || !config?.symbol) return;
    Promise.all([
      fetchUserTrades(currentUser.username).catch(() => null),
      fetchUserTrades(currentUser.username, { owner: 'SYSTEM', symbol: config.symbol }).catch(() => null),
    ]).then(([ownTrades, systemTrades]) => {
      if (ownTrades == null && systemTrades == null) return; // offline — keep localStorage data
      // The backend returns the user's own trades for every index — keep this symbol's only.
      const mapped = [
        ...(ownTrades    || []).filter(t => t.symbol === config.symbol).map(t => mapServerTrade(t, 'USER')),
        ...(systemTrades || []).map(t => mapServerTrade(t, 'SYSTEM')),
      ].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      // Set even when empty: an index with no trades must not keep another index's history.
      setTradeHistory(mapped);
    }).catch(() => {});
  }, [currentUser, config?.symbol]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load from backend on mount — replaces stale localStorage if server has newer data.
  useEffect(() => { refreshTradeHistory(); }, [refreshTradeHistory]);

  // Manual trades (isManual=true) can be REJECTED by the backend — no live price feed for
  // this symbol/strike right now (see UserTradeController.noLiveFeedReason), most commonly
  // SENSEX/BANKEX with nobody logged into Kite today. Awaited by GreekStopLossPanel so it
  // can show the rejection and stay in calculator mode instead of entering tracking mode.
  // SYSTEM/auto-detected trades (from TradingSetupPanel) are never subject to this guard —
  // they keep the original fire-and-forget, optimistic-save behavior unchanged.
  const handleRecordSignal = async (tradeEntry) => {
    const isManual = !!tradeEntry.isManual;

    if (isManual && currentUser?.username) {
      try {
        await saveUserTrade(currentUser.username, tradeEntry);
      } catch (err) {
        console.error('[TradeRecord] manual trade rejected by backend',
          err?.response?.status, err?.response?.data, tradeEntry);
        throw new Error(err?.response?.data?.error
          || 'Could not start tracking — the backend has no live price feed for this symbol/strike right now.');
      }
    }

    setTradeHistory(prev => [...prev, tradeEntry]);
    if (!isManual && currentUser?.username) {
      saveUserTrade(currentUser.username, tradeEntry)
        .catch(err => console.error(
          '[TradeRecord] saveUserTrade failed — trade is in localStorage only, admin dashboard will not show it.',
          err?.response?.status, err?.response?.data, tradeEntry
        ));
    }

    // ── Telegram: per-trade signal alert ──────────────────────────────────
    const sym        = symName(tradeEntry.symbol);
    const dir        = tradeEntry.dir === 'call' ? 'CALL' : 'PUT';
    const strike     = tradeEntry.entryStrike ? `${tradeEntry.entryStrike} ${dir}` : dir;
    const ltp        = tradeEntry.entryLTP != null ? `₹${tradeEntry.entryLTP.toFixed(2)}` : 'N/A';
    const symLotSize = tradeEntry.lotSize ?? 50;
    const numLots    = tradeEntry.numLots ?? 1;
    const fullQty    = symLotSize * numLots;
    const allocQty   = tradeEntry.allocatedLotSize ?? fullQty;

    let msg;
    if (isManual) {
      // ── Manual trade from Greek Stop Loss & Trade Tracker ──────────────
      const slPct      = tradeEntry.slPct ?? 0.30;
      const slPrice    = tradeEntry.entryLTP != null ? tradeEntry.entryLTP * (1 - slPct) : null;
      const slRiskUnit = tradeEntry.entryLTP != null ? tradeEntry.entryLTP * slPct : null;
      const totalRiskRs = slRiskUnit != null ? Math.round(slRiskUnit * allocQty) : null;
      msg = [
        `<b>🎯 BSG Manual Trade — ${sym}</b>`,
        `<i>${new Date(tradeEntry.timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} · ${fmtIST(tradeEntry.timestamp)} IST</i>`,
        ``,
        `<b>Side:</b> ${dir}  |  <b>Strike:</b> ${strike}`,
        `<b>Entry Premium:</b> ${ltp}`,
        `<b>Lots:</b> ${numLots} × ${symLotSize} = <b>${allocQty} qty</b>`,
        slPrice != null
          ? `<b>Stop Loss:</b> ${Math.round(slPct * 100)}% → ₹${slPrice.toFixed(2)}${totalRiskRs != null ? `  |  Max risk: ₹${totalRiskRs.toLocaleString('en-IN')}` : ''}`
          : '',
      ].filter(Boolean).join('\n');
    } else {
      // ── System signal trade ────────────────────────────────────────────
      const isOnRiskTrade = tradeEntry.finalVerdict === 'ON RISK';
      const verdict        = tradeEntry.finalVerdict === 'TAKE TRADE' ? 'TAKE TRADE ✅' : 'ON RISK ⚠️';
      const futSig = (() => {
        if (!gammaData?.syntheticFutures) return null;
        const tradeDir = tradeEntry.dir === 'call' ? 'BUY CE' : 'BUY PE';
        const fSig     = gammaData.futuresTradeSignal;
        const basis    = gammaData.futuresBasisState;
        const basisPct = gammaData.futuresBasisPct != null
          ? ` ${gammaData.futuresBasisPct > 0 ? '+' : ''}${gammaData.futuresBasisPct.toFixed(3)}%` : '';
        const fPrice   = Math.round(gammaData.syntheticFutures);
        const confirms = fSig === tradeDir;
        const icon     = confirms ? '✅' : (fSig === 'NO SIGNAL' ? '➖' : '⚠️');
        const status   = confirms ? 'CONFIRMS' : (fSig === 'NO SIGNAL' ? 'NEUTRAL' : 'CONFLICTS');
        return `${icon} ${status} — Basis: ${basis}${basisPct}, F=${fPrice}`;
      })();
      msg = [
        `<b>🔔 BSG Signal — ${sym}</b>`,
        `<i>${new Date(tradeEntry.timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} · ${fmtIST(tradeEntry.timestamp)} IST</i>`,
        ``,
        `<b>Signal:</b> ${tradeEntry.signal}  |  <b>Verdict:</b> ${verdict}  |  <b>Score:</b> ${tradeEntry.dir === 'call' ? tradeEntry.callScore : tradeEntry.putScore}/10`,
        `<b>Strike:</b> ${strike}  |  <b>ATM Entry LTP:</b> ${ltp}`,
        `<b>Lots:</b> ${numLots} × ${symLotSize} = ${allocQty} qty${isOnRiskTrade ? ` (${numLots} lot${numLots === 1 ? '' : 's'} — ON RISK)` : ` (${numLots} lot${numLots === 1 ? '' : 's'} — TAKE TRADE)`}  |  <b>Est. charges:</b> ₹${roundTripCharges(tradeEntry.symbol, tradeEntry.entryLTP, tradeEntry.entryLTP, allocQty)} round trip`,
        tradeEntry.dteCategory ? `<b>DTE:</b> ${tradeEntry.dteCategory}  |  <b>MTF:</b> ${tradeEntry.mtfAligned ? 'All aligned ✅' : 'Partial ⚠️'}` : '',
        futSig ? `<b>Synthetic Futures:</b> ${futSig}` : '',
        tradeEntry.srBlocked   ? `⛔ S/R Blocked`  : '',
        tradeEntry.pcrOpposing ? `⚠️ PCR opposing` : '',
      ].filter(Boolean).join('\n');
    }

    // Manual trades always notify — notificationsEnabled only gates auto-signal spam
    if (isManual || notificationsEnabled) {
      sendTelegramNotification(msg).catch(err =>
        console.warn('Signal Telegram failed:', err)
      );
    }
  };

  const handleMarkOutcome = (id, outcome) => {
    const trade = tradeHistory.find(t => t.id === id); // capture before state update
    setTradeHistory(prev => prev.map(t => t.id === id ? { ...t, outcome } : t));
    if (outcome === 'WIN')  setSessionTrades(prev => ({ ...prev, wins:   prev.wins   + 1 }));
    if (outcome === 'LOSS') setSessionTrades(prev => ({ ...prev, losses: prev.losses + 1 }));
    if (currentUser?.username) {
      patchUserTrade(currentUser.username, id, { outcome })
        .catch(err => console.error('[MarkOutcome] patchUserTrade failed', err?.response?.status, { id, outcome }));
    }

    // Telegram notification
    if (!trade) return;
    const sym      = symName(trade.symbol);
    const dir      = trade.dir === 'call' ? 'CALL' : 'PUT';
    const strike   = trade.entryStrike ? `${trade.entryStrike} ${dir}` : dir;
    const entryStr = trade.entryLTP != null ? `₹${trade.entryLTP.toFixed(2)}` : 'N/A';
    const icon     = outcome === 'WIN' ? '✅ WIN' : '❌ LOSS';
    const dateStr  = new Date(trade.timestamp).toLocaleDateString('en-IN',
      { day: '2-digit', month: 'short', year: 'numeric' });
    const pnlLine  = trade.pnlPts != null
      ? `<b>P&L:</b> ${trade.pnlPts >= 0 ? '+' : ''}${trade.pnlPts} pts`
      : null;

    // All-time (this symbol, every day ever recorded — NOT "this session" despite the old
    // label; tradeHistory itself is never date-scoped) and today-only breakdowns.
    const mkSymClosed      = tradeHistory.filter(t => t.symbol === trade.symbol && t.outcome !== 'OPEN' && t.id !== id);
    const mkAllWins        = mkSymClosed.filter(t => t.outcome === 'WIN').length  + (outcome === 'WIN'  ? 1 : 0);
    const mkAllLosses      = mkSymClosed.filter(t => t.outcome === 'LOSS').length + (outcome === 'LOSS' ? 1 : 0);
    const mkAllTotal       = mkAllWins + mkAllLosses;
    const mkAllWR          = mkAllTotal > 0 ? Math.round(mkAllWins / mkAllTotal * 100) : 0;
    const mkAllPnl         = parseFloat((mkSymClosed.reduce((s, t) => s + (t.pnlPts ?? 0), 0) + (trade.pnlPts ?? 0)).toFixed(1));

    const mkTodaySymClosed = mkSymClosed.filter(t => isToday(t.timestamp));
    const mkTodayWins      = mkTodaySymClosed.filter(t => t.outcome === 'WIN').length  + (outcome === 'WIN'  ? 1 : 0);
    const mkTodayLosses    = mkTodaySymClosed.filter(t => t.outcome === 'LOSS').length + (outcome === 'LOSS' ? 1 : 0);
    const mkTodayTotal     = mkTodayWins + mkTodayLosses;
    const mkTodayWR        = mkTodayTotal > 0 ? Math.round(mkTodayWins / mkTodayTotal * 100) : 0;
    const mkTodayPnl       = parseFloat((mkTodaySymClosed.reduce((s, t) => s + (t.pnlPts ?? 0), 0) + (trade.pnlPts ?? 0)).toFixed(1));

    const msg = [
      `<b>📊 BSG Trade Closed — ${sym} ${trade.isManual ? '(Manual)' : '(System)'}</b>`,
      `<i>${dateStr} · ${fmtIST(trade.timestamp)} IST</i>`,
      ``,
      `<b>Strike:</b> ${strike}`,
      `<b>Entry LTP:</b> ${entryStr}`,
      pnlLine,
      `<b>Outcome: ${icon}</b>  <i>(marked manually)</i>`,
      ``,
      `<b>Today [${sym}]:</b> ${mkTodayWins}W / ${mkTodayLosses}L${mkTodayTotal > 0 ? ` · ${mkTodayWR}% WR` : ''} · ${mkTodayPnl >= 0 ? '+' : ''}${mkTodayPnl} pts`,
      `<b>All-time [${sym}]:</b> ${mkAllWins}W / ${mkAllLosses}L${mkAllTotal > 0 ? ` · ${mkAllWR}% WR` : ''} · ${mkAllPnl >= 0 ? '+' : ''}${mkAllPnl} pts`,
    ].filter(Boolean).join('\n');

    sendTelegramNotification(msg).catch(err =>
      console.warn('Mark-outcome Telegram failed:', err)
    );
  };

  const handleClearHistory = () => {
    // Clears only the caller's own trades on this index; SYSTEM trades stay on the server,
    // so keep them in view rather than hiding them until the next refresh.
    setTradeHistory(prev => prev.filter(t => t.source === 'SYSTEM'));
    if (currentUser?.username) {
      clearUserTrades(currentUser.username, config.symbol).catch(() => {});
    }
  };

  // Gamma-flip crossover and chain-sweep Telegram alerts are now sent autonomously by
  // TradingSignalService on the backend (see gammaFlipAlertDir/chainSweepAlertDir there) —
  // the client-side handleGammaFlipCrossover/handleChainSweepAlert senders were removed to
  // avoid duplicate Telegram messages when a tab happens to be open. The on-screen crossover
  // badge (setGammaFlipCrossover above) and the chain-sweep banner in TradingSetupPanel are
  // unaffected — only the tab-dependent Telegram sending was retired.

  // Fires on circuit-breaker trip/release/reset so trading pause/resume is visible
  // without the dashboard open. Wording matches the on-screen badge text.
  const handleCircuitBreaker = ({ event, losses, dir, symbol: sym, cooldownMin, sweepPolls }) => {
    if (!notificationsEnabled) return;
    const symDisplay = symName(sym || config.symbol);
    const timeStr = new Date().toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
    });
    let msg;
    if (event === 'TRIP') {
      msg = [
        `<b>🛑 CIRCUIT BREAKER TRIPPED — ${symDisplay}</b>`,
        `<i>${timeStr} IST</i>`,
        ``,
        `${losses} losses this session — trading is now locked.`,
        `Mandatory ${cooldownMin}-min cooldown, then only resumes if a chain sweep holds for ${sweepPolls}+ consecutive polls in one direction.`,
      ].join('\n');
    } else if (event === 'RELEASE') {
      msg = [
        `<b>✅ Circuit Breaker Released — ${symDisplay}</b>`,
        `<i>${timeStr} IST</i>`,
        ``,
        `Cooldown passed and a chain sweep held for ${sweepPolls}+ consecutive polls in the ${dir === 'call' ? 'bullish' : 'bearish'} direction.`,
        `Trading has resumed.`,
      ].join('\n');
    } else {
      msg = [
        `<b>🔄 Circuit Breaker Reset — ${symDisplay}</b>`,
        `<i>${timeStr} IST</i>`,
        `Session was manually cleared — trading has resumed.`,
      ].join('\n');
    }
    sendTelegramNotification(msg).catch(err => console.warn('Circuit breaker Telegram failed:', err));
  };

  const handleSquareOff = (trade, exitLTP, exitReason, exitTime) => {
    const resolvedExitTime = exitTime ?? new Date().toISOString();
    const pnlPts   = (exitLTP != null && trade.entryLTP != null)
      ? parseFloat((exitLTP - trade.entryLTP).toFixed(2))
      : null;
    const grossRs  = pnlPts != null ? Math.round(pnlPts * (trade.allocatedLotSize ?? trade.lotSize ?? 50)) : null;
    const chargesRs = roundTripCharges(trade.symbol, trade.entryLTP, exitLTP, trade.allocatedLotSize ?? trade.lotSize ?? 50);
    const netRs    = grossRs != null ? grossRs - chargesRs : null;
    const outcome  = netRs != null ? (netRs > 0 ? 'WIN' : 'LOSS') : 'OPEN';

    setTradeHistory(prev => prev.map(t =>
      t.id === trade.id
        ? { ...t, exitLTP, exitTime: resolvedExitTime, exitReason: exitReason ?? 'Manual Square Off', pnlPts, outcome }
        : t
    ));
    if (outcome === 'WIN')  setSessionTrades(prev => ({ ...prev, wins:   prev.wins   + 1 }));
    if (outcome === 'LOSS') setSessionTrades(prev => ({ ...prev, losses: prev.losses + 1 }));
    if (currentUser?.username) {
      patchUserTrade(currentUser.username, trade.id, {
        exitLTP, exitTime: resolvedExitTime,
        exitReason: exitReason ?? 'Manual Square Off',
        pnlPts, outcome,
      }).catch(err => {
        const status = err?.response?.status;
        console.error('[TradeClose] patchUserTrade failed', status, { tradeId: trade.id, outcome, pnlPts });
        if (status === 404) {
          // Trade was never saved (initial saveUserTrade failed). Re-save the completed trade now
          // so the admin dashboard and day-end report still see it with the correct outcome.
          const completedTrade = {
            ...trade,
            exitLTP, exitTime: resolvedExitTime,
            exitReason: exitReason ?? 'Manual Square Off',
            pnlPts, outcome,
          };
          saveUserTrade(currentUser.username, completedTrade)
            .catch(e => console.error('[TradeClose] fallback saveUserTrade also failed', e?.response?.status));
        }
      });
    }

    // ── Telegram: square-off notification ─────────────────────────────────
    const sym    = symName(trade.symbol);
    const dir    = trade.dir === 'call' ? 'CALL' : 'PUT';
    const strike = trade.entryStrike ? `${trade.entryStrike} ${dir}` : dir;
    const entryStr = trade.entryLTP != null ? `₹${trade.entryLTP.toFixed(2)}` : 'N/A';
    const exitStr  = exitLTP        != null ? `₹${exitLTP.toFixed(2)}`        : 'N/A';
    const outcomeIcon = outcome === 'WIN' ? '✅ WIN' : outcome === 'LOSS' ? '❌ LOSS' : '—';

    // All-time (this symbol, every day ever recorded — NOT "this session" despite the old
    // label; tradeHistory itself is never date-scoped) and today-only breakdowns.
    const sqSymClosed      = tradeHistory.filter(t => t.symbol === trade.symbol && t.outcome !== 'OPEN');
    const sqAllWins        = sqSymClosed.filter(t => t.outcome === 'WIN').length  + (outcome === 'WIN'  ? 1 : 0);
    const sqAllLosses      = sqSymClosed.filter(t => t.outcome === 'LOSS').length + (outcome === 'LOSS' ? 1 : 0);
    const sqAllTotal       = sqAllWins + sqAllLosses;
    const sqAllWR          = sqAllTotal > 0 ? Math.round(sqAllWins / sqAllTotal * 100) : 0;
    const sqAllPnl         = parseFloat((sqSymClosed.reduce((s, t) => s + (t.pnlPts ?? 0), 0) + (pnlPts ?? 0)).toFixed(1));

    const sqTodaySymClosed = sqSymClosed.filter(t => isToday(t.timestamp));
    const sqTodayWins      = sqTodaySymClosed.filter(t => t.outcome === 'WIN').length  + (outcome === 'WIN'  ? 1 : 0);
    const sqTodayLosses    = sqTodaySymClosed.filter(t => t.outcome === 'LOSS').length + (outcome === 'LOSS' ? 1 : 0);
    const sqTodayTotal     = sqTodayWins + sqTodayLosses;
    const sqTodayWR        = sqTodayTotal > 0 ? Math.round(sqTodayWins / sqTodayTotal * 100) : 0;
    const sqTodayPnl       = parseFloat((sqTodaySymClosed.reduce((s, t) => s + (t.pnlPts ?? 0), 0) + (pnlPts ?? 0)).toFixed(1));

    // Tags the message so a coincidentally-identical manual trade and system auto-trade on
    // the same strike (both reading the same live tick) never look like the same position
    // closing — see the incident where a manual BANKNIFTY trade and TradingSetupPanel's own
    // auto-trade opened at an identical strike/premium and the square-off looked ambiguous.
    const sourceTag = trade.isManual ? '(Manual)' : '(System)';

    // Capital used = the premium actually paid to open this position — for a long options
    // buyer that premium IS the capital at risk (no separate margin). Mirrors the backend's
    // buildSystemExitMessage (TradingSignalService.java) so a manual square-off and a
    // SYSTEM-engine square-off report this the same way.
    const capitalQty     = trade.allocatedLotSize ?? trade.lotSize ?? 50;
    const capitalUsedRs  = trade.entryLTP != null ? trade.entryLTP * capitalQty : null;
    const pctGainOnCapital = (netRs != null && capitalUsedRs > 0) ? (netRs / capitalUsedRs * 100) : null;

    const msg = [
      `<b>📊 BSG Square-Off — ${sym} ${sourceTag}</b>`,
      `<i>${new Date(trade.timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} · ${fmtIST(trade.timestamp)} → ${fmtIST(resolvedExitTime)} IST</i>`,
      ``,
      `<b>Strike:</b> ${strike}`,
      `<b>Reason:</b> ${exitReason ?? 'Manual Square Off'}`,
      `<b>Entry LTP:</b> ${entryStr}  →  <b>Exit LTP:</b> ${exitStr}`,
      pnlPts  != null ? `<b>P&L:</b> ${pnlPts >= 0 ? '+' : ''}${pnlPts} pts` : '',
      grossRs != null ? `<b>Gross P&L:</b> ${grossRs >= 0 ? '+' : ''}₹${Math.abs(grossRs).toLocaleString('en-IN')}` : '',
      `<b>Charges:</b> -₹${chargesRs} (brokerage, STT, exchange, GST, stamp)`,
      netRs   != null ? `<b>Net P&L: ${netRs >= 0 ? '+' : ''}₹${netRs.toLocaleString('en-IN')}  ${outcomeIcon}</b>` : `<b>Outcome: ${outcomeIcon}</b>`,
      capitalUsedRs != null
        ? `<b>Capital Used:</b> ₹${Math.round(capitalUsedRs).toLocaleString('en-IN')}`
          + (pctGainOnCapital != null ? `  |  <b>Return:</b> ${pctGainOnCapital >= 0 ? '+' : ''}${pctGainOnCapital.toFixed(2)}% on capital` : '')
        : '',
      ``,
      `<b>Today [${sym}]:</b> ${sqTodayWins}W / ${sqTodayLosses}L${sqTodayTotal > 0 ? ` · ${sqTodayWR}% WR` : ''} · ${sqTodayPnl >= 0 ? '+' : ''}${sqTodayPnl} pts`,
      `<b>All-time [${sym}]:</b> ${sqAllWins}W / ${sqAllLosses}L${sqAllTotal > 0 ? ` · ${sqAllWR}% WR` : ''} · ${sqAllPnl >= 0 ? '+' : ''}${sqAllPnl} pts`,
    ].filter(Boolean).join('\n');

    sendTelegramNotification(msg).catch(err =>
      console.warn('Square-off Telegram failed:', err)
    );
  };

  // Manual-trade trail-SL/milestone Telegram alerts are now sent autonomously by
  // ManualTradeTrackingService on the backend — handleTrailSL (client-side, fired only from
  // GreekStopLossPanel's now-removed "Apply Trail SL" button) was removed.

  const generateEODReport = useCallback(() => {
    const now   = new Date();
    const today = now.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const todayTrades = tradeHistory.filter(t => {
      const d = new Date(t.timestamp);
      return d.getDate()     === now.getDate() &&
             d.getMonth()    === now.getMonth() &&
             d.getFullYear() === now.getFullYear();
    });
    if (todayTrades.length === 0) return;

    // ── Per-trade calculations ─────────────────────────────────────────────
    // For OPEN trades still active at EOD, estimate P&L from the last known LTP.
    // outcome stays 'OPEN' — estimatedExitLTP/estimatedPnlPts are display-only.
    const enriched = todayTrades.map(t => {
      let { exitLTP, pnlPts, outcome } = t;
      let estimatedExitLTP = null;
      let estimatedPnlPts  = null;

      if (outcome === 'OPEN' && t.entryLTP != null && gammaData) {
        const strikeRow = t.entryStrike && gammaData.strikeData
          ? gammaData.strikeData.find(s => s.strikePrice === t.entryStrike) : null;
        const latestLTP = strikeRow
          ? (t.dir === 'call' ? (strikeRow.ceLTP ?? null) : (strikeRow.peLTP ?? null))
          : (t.dir === 'call' ? (gammaData.atmCallLTP ?? null) : (gammaData.atmPutLTP ?? null));
        if (latestLTP != null) {
          estimatedExitLTP = latestLTP;
          estimatedPnlPts  = parseFloat((latestLTP - t.entryLTP).toFixed(2));
          // outcome intentionally stays 'OPEN' — never count open trades as closed
        }
      }

      const grossPnlRs = pnlPts != null ? Math.round(pnlPts * (t.allocatedLotSize ?? t.lotSize ?? 50)) : null;
      const brokerage  = outcome !== 'OPEN'
        ? roundTripCharges(t.symbol, t.entryLTP, exitLTP, t.allocatedLotSize ?? t.lotSize ?? 50) : 0;
      const netPnlRs   = grossPnlRs != null ? grossPnlRs - brokerage : null;
      return { ...t, exitLTP, pnlPts, outcome, grossPnlRs, brokerage, netPnlRs,
               estimatedExitLTP, estimatedPnlPts };
    });

    // ── Sheet 1: Trade Log ─────────────────────────────────────────────────
    const tradeLogHeaders = [
      'Entry Time', 'Exit Time', 'Symbol', 'Strike', 'Type',
      'Signal', 'Verdict', 'Lot Size',
      'Entry LTP (₹)', 'Exit LTP (₹)', 'P&L (pts)',
      'Gross P&L (₹)', 'Brokerage (₹)', 'Net P&L (₹)',
      'Outcome', 'DTE', 'MTF Aligned', 'S/R Blocked', 'PCR Opposing',
      'OI Setup', 'BB Signal',
    ];
    const tradeLogRows = enriched.map(t => ([
      new Date(t.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }),
      t.exitTime ? new Date(t.exitTime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—',
      symName(t.symbol),
      t.entryStrike ?? '—',
      t.dir === 'call' ? 'CE' : 'PE',
      t.signal,
      t.finalVerdict,
      (() => {
        const symLS  = t.lotSize ?? 50;
        const nLots  = t.numLots ?? 1;
        const alloc  = t.allocatedLotSize ?? symLS;
        const full   = symLS * nLots;
        return alloc < full ? `${alloc} qty (½ of ${nLots}L)` : `${alloc} qty (${nLots}L)`;
      })(),
      t.entryLTP  != null ? parseFloat(t.entryLTP.toFixed(2))  : '—',
      t.exitLTP   != null ? parseFloat(t.exitLTP.toFixed(2))   : '—',
      t.pnlPts    != null ? parseFloat(t.pnlPts.toFixed(2))    : '—',
      t.grossPnlRs ?? '—',
      t.brokerage > 0 ? t.brokerage : '—',
      t.netPnlRs  ?? '—',
      t.outcome,
      t.dte       ?? '—',
      t.mtfAligned  ? 'Yes' : 'No',
      t.srBlocked   ? 'Yes' : 'No',
      t.pcrOpposing ? 'Yes' : 'No',
      t.oiSetupSignal ?? '—',
      t.vwapSignal    ?? '—',
    ]));
    const tradeLogSheet = XLSX.utils.aoa_to_sheet([tradeLogHeaders, ...tradeLogRows]);
    // Column widths
    tradeLogSheet['!cols'] = [
      12,12,12,8,5,14,12,10,14,13,10,14,14,12,8,5,12,12,14,14,12
    ].map(w => ({ wch: w }));

    // ── Sheet 2: P&L Summary ───────────────────────────────────────────────
    const closedTrades  = enriched.filter(t => t.outcome !== 'OPEN');
    const wins          = closedTrades.filter(t => t.outcome === 'WIN').length;
    const losses        = closedTrades.filter(t => t.outcome === 'LOSS').length;
    const openCount     = enriched.filter(t => t.outcome === 'OPEN').length;
    const totalGrossRs  = closedTrades.reduce((s, t) => s + (t.grossPnlRs ?? 0), 0);
    const totalBrokerageRs = closedTrades.reduce((s, t) => s + t.brokerage, 0);
    const totalNetRs    = totalGrossRs - totalBrokerageRs;
    const winRate       = closedTrades.length > 0 ? Math.round(wins / closedTrades.length * 100) : 0;

    // Per-symbol breakdown (only NIFTY and BANKNIFTY get 1-lot P&L callout)
    const symbols = [...new Set(enriched.map(t => t.symbol))];
    const symbolRows = symbols.map(sym => {
      const g  = closedTrades.filter(t => t.symbol === sym);
      const gW = g.filter(t => t.outcome === 'WIN').length;
      const gross  = g.reduce((s, t) => s + (t.grossPnlRs ?? 0), 0);
      const broker = g.reduce((s, t) => s + t.brokerage, 0);
      return [symName(sym), g.length, gW, g.length - gW,
              g.length > 0 ? Math.round(gW / g.length * 100) + '%' : '—',
              gross, broker, gross - broker];
    });

    const summaryData = [
      // ── Section A: Overall ──────────────────────────────────────────────
      ['BSG TRADERS — EOD P&L SUMMARY', today],
      [],
      ['OVERALL', ''],
      ['Total Trades',          enriched.length],
      ['Closed',                closedTrades.length],
      ['Open (no square-off)',  openCount],
      ['Wins',                  wins],
      ['Losses',                losses],
      ['Win Rate',              closedTrades.length > 0 ? winRate + '%' : '—'],
      [],
      ['Gross P&L',             totalGrossRs],
      ['Total Brokerage',       -totalBrokerageRs],
      ['NET P&L',               totalNetRs],
      [],
      // ── Section B: Symbol breakdown ────────────────────────────────────
      ['SYMBOL BREAKDOWN', '', '', '', '', '', '', ''],
      ['Symbol', 'Closed Trades', 'Wins', 'Losses', 'Win%', 'Gross P&L (₹)', 'Brokerage (₹)', 'Net P&L (₹)'],
      ...symbolRows,
      [],
      // ── Section C: Charges explanation ────────────────────────────────
      ['CHARGES BREAKDOWN', ''],
      ['Brokerage per order (buy or sell)', `₹${BROKERAGE_PER_ORDER}`],
      ['Taxes & fees', 'STT 0.15% sell · exchange 0.035% (BSE 0.0325%) · SEBI · stamp 0.003% buy · GST 18%'],
      ['Closed trades',                   closedTrades.length],
      ['Total charges',                   `₹${totalBrokerageRs}`],
      [],
      ['NOTE: Charges apply only to closed (squared-off) trades.', ''],
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    summarySheet['!cols'] = [{ wch: 32 }, { wch: 16 }, { wch: 10 }, { wch: 10 }, { wch: 8 }, { wch: 16 }, { wch: 16 }, { wch: 14 }];

    // ── Build & download workbook ──────────────────────────────────────────
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, tradeLogSheet, 'Trade Log');
    XLSX.utils.book_append_sheet(wb, summarySheet,  'P&L Summary');

    const fileName = `BSG-Trade-Report-${today.replace(/ /g, '-')}.xlsx`;
    XLSX.writeFile(wb, fileName);
    // Local download only — the backend (EodReportService, 15:40 IST market-close cron) is
    // now the sole sender of the EOD Telegram text + XLSX attachment. See note above.
  }, [tradeHistory, gammaData]);

  // ── Shared engine: weekly / monthly reports ─────────────────────────────
  // Produces a 4-sheet XLSX:
  //   1. Trade Journal  — every trade with entry conditions + exit reason
  //   2. P&L by Expiry  — grouped by expiryDate, then by symbol
  //   3. Breakdown      — daily (weekly report) or weekly (monthly report)
  //   4. P&L Summary    — overall + symbol + expiry totals
  const generatePeriodReport = useCallback((periodType, periodLabel, periodTrades) => {
    if (periodTrades.length === 0) {
      alert(`No trades found for ${periodLabel}.`);
      return;
    }

    // Enrich with computed P&L (same logic as EOD)
    const enriched = periodTrades.map(t => {
      let { exitLTP, pnlPts, outcome } = t;
      if (outcome === 'OPEN' && t.entryLTP != null && gammaData) {
        const strikeRow = t.entryStrike && gammaData.strikeData
          ? gammaData.strikeData.find(s => s.strikePrice === t.entryStrike) : null;
        const ltp = strikeRow
          ? (t.dir === 'call' ? (strikeRow.ceLTP ?? null) : (strikeRow.peLTP ?? null))
          : (t.dir === 'call' ? (gammaData.atmCallLTP ?? null) : (gammaData.atmPutLTP ?? null));
        if (ltp != null) {
          exitLTP = ltp; pnlPts = parseFloat((ltp - t.entryLTP).toFixed(2));
          const qty = t.allocatedLotSize ?? t.lotSize ?? 50;
          // WIN only after all charges, same as the backend's TradeCosts.outcome
          outcome = pnlPts * qty - roundTripCharges(t.symbol, t.entryLTP, ltp, qty) > 0 ? 'WIN' : 'LOSS';
        }
      }
      const grossPnlRs = pnlPts != null ? Math.round(pnlPts * (t.allocatedLotSize ?? t.lotSize ?? 50)) : null;
      const brokerage  = outcome !== 'OPEN'
        ? roundTripCharges(t.symbol, t.entryLTP, exitLTP, t.allocatedLotSize ?? t.lotSize ?? 50) : 0;
      const netPnlRs   = grossPnlRs != null ? grossPnlRs - brokerage : null;
      return { ...t, exitLTP, pnlPts, outcome, grossPnlRs, brokerage, netPnlRs };
    });

    // ── Sheet 1: Trade Journal ──────────────────────────────────────────
    const condHdr = ['Cond 1 (BB Mid)', 'Cond 2 (OI Setup)', 'Cond 3 (CE/PE OI)',
                     'Cond 4 (OI Writer)', 'Cond 5 (Vol/Sweep)', 'Cond 6 (IV)'];
    const journalHeaders = [
      'Date', 'Entry Time', 'Exit Time', 'Symbol', 'Expiry Date', 'Strike', 'Type',
      'Signal', 'Verdict', 'Entry ₹', 'Exit ₹', 'P&L Pts',
      'Gross P&L ₹', 'Brokerage ₹', 'Net P&L ₹',
      'Outcome', 'Exit Reason', 'DTE', 'MTF Aligned', 'S/R Blocked', 'PCR Opposing',
      'OI Setup', 'BB Signal', ...condHdr,
    ];
    const journalRows = enriched.map(t => {
      const conds = t.entryConditions ?? [];
      return [
        new Date(t.timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
        new Date(t.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }),
        t.exitTime ? new Date(t.exitTime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—',
        symName(t.symbol),
        t.expiryDate ?? '—',
        t.entryStrike ?? '—',
        t.dir === 'call' ? 'CE' : 'PE',
        t.signal, t.finalVerdict,
        t.entryLTP  != null ? parseFloat(t.entryLTP.toFixed(2))  : '—',
        t.exitLTP   != null ? parseFloat(t.exitLTP.toFixed(2))   : '—',
        t.pnlPts    != null ? parseFloat(t.pnlPts.toFixed(2))    : '—',
        t.grossPnlRs ?? '—',
        t.brokerage > 0 ? t.brokerage : '—',
        t.netPnlRs  ?? '—',
        t.outcome,
        t.exitReason ?? '—',
        t.dte ?? '—',
        t.mtfAligned  ? 'Yes' : 'No',
        t.srBlocked   ? 'Yes' : 'No',
        t.pcrOpposing ? 'Yes' : 'No',
        t.oiSetupSignal ?? '—',
        t.vwapSignal    ?? '—',
        ...Array.from({ length: 6 }, (_, i) => {
          const c = conds[i];
          return c ? (c.met ? `✓ ${c.detail ?? c.label}` : `✗ ${c.detail ?? c.label}`) : '—';
        }),
      ];
    });
    const journalSheet = XLSX.utils.aoa_to_sheet([journalHeaders, ...journalRows]);
    journalSheet['!cols'] = [
      10,10,10,10,12,8,5,14,12,10,10,10,14,12,12,
      8,16,5,12,10,12,14,14,22,22,22,22,22,22,
    ].map(w => ({ wch: w }));

    // ── Sheet 2: P&L by Expiry ─────────────────────────────────────────
    const expiryGroups = {};
    enriched.forEach(t => {
      const k = t.expiryDate ?? 'Unknown Expiry';
      if (!expiryGroups[k]) expiryGroups[k] = [];
      expiryGroups[k].push(t);
    });
    const expiryRows = [
      ['Expiry Date', 'Symbol', 'Trades', 'Wins', 'Losses', 'Win%', 'Gross P&L ₹', 'Brokerage ₹', 'Net P&L ₹'],
    ];
    Object.entries(expiryGroups).sort(([a], [b]) => a.localeCompare(b)).forEach(([exp, etrades]) => {
      const syms = [...new Set(etrades.map(t => t.symbol))];
      syms.forEach(sym => {
        const g  = etrades.filter(t => t.symbol === sym && t.outcome !== 'OPEN');
        const gW = g.filter(t => t.outcome === 'WIN').length;
        const gGross = g.reduce((s, t) => s + (t.grossPnlRs ?? 0), 0);
        const gBroke = g.reduce((s, t) => s + (t.brokerage ?? 0), 0);
        expiryRows.push([exp, symName(sym), g.length, gW, g.length - gW,
          g.length > 0 ? Math.round(gW / g.length * 100) + '%' : '—',
          gGross, gBroke, gGross - gBroke]);
      });
      const all  = etrades.filter(t => t.outcome !== 'OPEN');
      const allW = all.filter(t => t.outcome === 'WIN').length;
      const allG = all.reduce((s, t) => s + (t.grossPnlRs ?? 0), 0);
      const allB = all.reduce((s, t) => s + (t.brokerage ?? 0), 0);
      expiryRows.push(['', `── EXPIRY TOTAL (${exp})`, all.length, allW, all.length - allW,
        all.length > 0 ? Math.round(allW / all.length * 100) + '%' : '—',
        allG, allB, allG - allB]);
      expiryRows.push([]);
    });
    const expirySheet = XLSX.utils.aoa_to_sheet(expiryRows);
    expirySheet['!cols'] = [14,12,8,6,8,7,14,14,12].map(w => ({ wch: w }));

    // ── Sheet 3: Daily / Weekly Breakdown ──────────────────────────────
    const isMonthly = periodType === 'MONTHLY';
    const getGroupKey = ts => {
      const d = new Date(ts);
      if (isMonthly) {
        // ISO week: Mon=1 … Sun=7; week number within year
        const thu = new Date(d); thu.setDate(d.getDate() - ((d.getDay() + 6) % 7) + 3);
        const wk  = Math.ceil(((thu - new Date(thu.getFullYear(), 0, 4)) / 864e5 + 4) / 7);
        const mon = new Date(d); mon.setDate(d.getDate() - (d.getDay() + 6) % 7);
        const fri = new Date(mon); fri.setDate(mon.getDate() + 4);
        return `Wk ${wk} (${mon.toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}–${fri.toLocaleDateString('en-IN',{day:'2-digit',month:'short'})})`;
      }
      return d.toLocaleDateString('en-IN', { weekday:'short', day:'2-digit', month:'short', year:'numeric' });
    };
    const breakGroups = {};
    enriched.forEach(t => {
      const k = getGroupKey(t.timestamp);
      if (!breakGroups[k]) breakGroups[k] = [];
      breakGroups[k].push(t);
    });
    const breakRows = [
      [isMonthly ? 'Week' : 'Date', 'Trades', 'Wins', 'Losses', 'Win%', 'Gross P&L ₹', 'Brokerage ₹', 'Net P&L ₹'],
    ];
    Object.entries(breakGroups).forEach(([key, btrades]) => {
      const cl = btrades.filter(t => t.outcome !== 'OPEN');
      const bW = cl.filter(t => t.outcome === 'WIN').length;
      const bG = cl.reduce((s, t) => s + (t.grossPnlRs ?? 0), 0);
      const bB = cl.reduce((s, t) => s + (t.brokerage ?? 0), 0);
      breakRows.push([key, cl.length, bW, cl.length - bW,
        cl.length > 0 ? Math.round(bW / cl.length * 100) + '%' : '—',
        bG, bB, bG - bB]);
    });
    const breakSheet = XLSX.utils.aoa_to_sheet(breakRows);
    breakSheet['!cols'] = [26,8,6,8,7,14,14,12].map(w => ({ wch: w }));

    // ── Sheet 4: P&L Summary ───────────────────────────────────────────
    const closed    = enriched.filter(t => t.outcome !== 'OPEN');
    const wins      = closed.filter(t => t.outcome === 'WIN').length;
    const losses    = closed.filter(t => t.outcome === 'LOSS').length;
    const tGross    = closed.reduce((s, t) => s + (t.grossPnlRs ?? 0), 0);
    const tBroke    = closed.reduce((s, t) => s + (t.brokerage  ?? 0), 0);
    const tNet      = tGross - tBroke;
    const wr        = closed.length > 0 ? Math.round(wins / closed.length * 100) : 0;
    const symBreak  = [...new Set(enriched.map(t => t.symbol))].map(sym => {
      const g = closed.filter(t => t.symbol === sym);
      const w = g.filter(t => t.outcome === 'WIN').length;
      const g2 = g.reduce((s,t)=>s+(t.grossPnlRs??0),0), b2 = g.reduce((s,t)=>s+(t.brokerage??0),0);
      return [symName(sym), g.length, w, g.length-w, g.length>0?Math.round(w/g.length*100)+'%':'—', g2, b2, g2-b2];
    });
    const expBreak = Object.entries(expiryGroups).map(([exp, et]) => {
      const g = et.filter(t => t.outcome !== 'OPEN');
      const w = g.filter(t => t.outcome === 'WIN').length;
      const g2 = g.reduce((s,t)=>s+(t.grossPnlRs??0),0), b2 = g.reduce((s,t)=>s+(t.brokerage??0),0);
      return [exp, g.length, w, g.length-w, g.length>0?Math.round(w/g.length*100)+'%':'—', g2, b2, g2-b2];
    });
    const summaryData = [
      [`BSG TRADERS — ${periodType} P&L SUMMARY`, periodLabel], [],
      ['OVERALL', ''],
      ['Period', periodLabel], ['Total Trades', enriched.length],
      ['Closed', closed.length], ['Open (no square-off)', enriched.length - closed.length],
      ['Wins', wins], ['Losses', losses],
      ['Win Rate', closed.length > 0 ? wr + '%' : '—'], [],
      ['Gross P&L (₹)', tGross], ['Total Brokerage (₹)', -tBroke], ['NET P&L (₹)', tNet], [],
      ['SYMBOL BREAKDOWN','','','','','','',''],
      ['Symbol','Closed','Wins','Losses','Win%','Gross P&L ₹','Brokerage ₹','Net P&L ₹'],
      ...symBreak, [],
      ['EXPIRY BREAKDOWN','','','','','','',''],
      ['Expiry','Closed','Wins','Losses','Win%','Gross P&L ₹','Brokerage ₹','Net P&L ₹'],
      ...expBreak, [],
      ['NOTE: Brokerage charged only on closed (squared-off) trades.', ''],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    summarySheet['!cols'] = [32,16,10,10,8,16,16,14].map(w => ({ wch: w }));

    // ── Build workbook & download ──────────────────────────────────────
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, journalSheet, 'Trade Journal');
    XLSX.utils.book_append_sheet(wb, expirySheet,  'P&L by Expiry');
    XLSX.utils.book_append_sheet(wb, breakSheet,   isMonthly ? 'Weekly Breakdown' : 'Daily Breakdown');
    XLSX.utils.book_append_sheet(wb, summarySheet, 'P&L Summary');
    const safe = periodLabel.replace(/[/ ]/g, '-').replace(/[^a-zA-Z0-9\-]/g, '');
    XLSX.writeFile(wb, `BSG-${periodType}-Report-${safe}.xlsx`);
  }, [gammaData]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateWeeklyReport = useCallback(() => {
    const now = new Date();
    const dow  = now.getDay(); // 0=Sun
    const diffMon = dow === 0 ? -6 : 1 - dow;
    const mon = new Date(now); mon.setDate(now.getDate() + diffMon); mon.setHours(0,0,0,0);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);       sun.setHours(23,59,59,999);
    const label = `${mon.toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}–${sun.toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}`;
    generatePeriodReport('WEEKLY', label, tradeHistory.filter(t => {
      const d = new Date(t.timestamp); return d >= mon && d <= sun;
    }));
  }, [tradeHistory, generatePeriodReport]);

  const generateMonthlyReport = useCallback(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    const label = now.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    generatePeriodReport('MONTHLY', label, tradeHistory.filter(t => {
      const d = new Date(t.timestamp); return d >= start && d <= end;
    }));
  }, [tradeHistory, generatePeriodReport]);

  // NOTE: this used to auto-fire generateEODReport() at 15:40 IST from every open browser
  // tab — each tab built the report from its own local, symbol-scoped tradeHistory/gammaData
  // state, so multiple open tabs produced multiple conflicting Telegram messages (or none, if
  // no tab happened to be open). The backend now owns the authoritative EOD Telegram report +
  // XLSX send (EodReportService, fired once from the 15:40 IST market-close cron, querying the
  // DB directly for every symbol). generateEODReport below is now a LOCAL-DOWNLOAD-ONLY export
  // for whoever clicks the manual "EOD" button — it no longer sends anything to Telegram.

  // Force this dashboard onto the backend's actual tracked expiry (theta-gate auto-adjustment)
  // as soon as it's known to differ — the SYSTEM engine always trades ITS OWN activeExpiry
  // regardless of what's shown here, so silently letting them drift means analysis/gamma HTTP
  // polling (fetchAnalysis) keeps querying the wrong contract. Records the overridden value in
  // expiryAutoSyncedFrom so the warning banner below can explain what changed. Respects a
  // deliberate manual expiry pick (manualExpiryRef) — this only forces a correction once, on
  // whatever the first mismatching gammaData delivery is (typically page load).
  useEffect(() => {
    const backendExpiry = gammaData?.activeExpiry;
    if (!backendExpiry || manualExpiryRef.current) return;
    if (backendExpiry !== activeConfigRef.current.expiryDate) {
      setExpiryAutoSyncedFrom(prev => prev ?? activeConfigRef.current.expiryDate);
      activeConfigRef.current.expiryDate = backendExpiry;
      setActiveExpiryDate(backendExpiry);
      // The dropdown must offer the synced expiry, or the <select> falls back to showing its
      // first option (Kite mode lists only the expiry the dashboard was opened with).
      setAvailableExpiries(prev => prev.includes(backendExpiry)
        ? prev
        : (config.kiteMode ? [backendExpiry] : [...prev, backendExpiry]));
    }
  }, [gammaData?.activeExpiry]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll the backend's actual SYSTEM engine status for this symbol (circuit breaker, today's
  // win/loss count, open position) and sync the session state onto it — for every user. The
  // backend engine is the only one that trades; TradingSetupPanel just mirrors it.
  useEffect(() => {
    if (!currentUser?.username || !config.symbol) return;
    let cancelled = false;
    const poll = () => {
      getSystemStatus(config.symbol, currentUser?.username)
        .then(status => {
          if (cancelled) return;
          setBackendSystemStatus(status);
          setSessionTrades({ wins: status.winsToday ?? 0, losses: status.lossesToday ?? 0 });
        })
        .catch(() => {}); // keep last-known status on a transient fetch failure
    };
    poll();
    const id = setInterval(poll, liveRefreshInterval);
    return () => { cancelled = true; clearInterval(id); };
  }, [currentUser?.username, config.symbol, liveRefreshInterval]); // eslint-disable-line react-hooks/exhaustive-deps

  // The SYSTEM engine opens/closes positions on the backend with no event to this tab, so
  // tradeHistory (Win Rate Tracker + Recent trades) would otherwise only pick them up on a
  // page reload. Refetch whenever the polled status shows a position opened, closed, or a
  // new trade counted.
  const systemStatusSigRef = useRef(null);
  useEffect(() => {
    if (!backendSystemStatus) return;
    const op = backendSystemStatus.openPosition;
    const os = backendSystemStatus.openStrategy;
    const sig = [
      op ? `${op.timestamp}|${op.entryStrike}|${op.dir}` : 'flat',
      os ? `${os.timestamp}|${os.structure}` : 'no-sell',
      backendSystemStatus.tradesToday, backendSystemStatus.winsToday, backendSystemStatus.lossesToday,
    ].join('#');
    if (systemStatusSigRef.current != null && systemStatusSigRef.current !== sig) refreshTradeHistory();
    systemStatusSigRef.current = sig;
  }, [backendSystemStatus, refreshTradeHistory]);

  // ── Panel resize ─────────────────────────────────────────────────────────
  const [leftWidth,  setLeftWidth]  = useState(320);
  const [rightWidth, setRightWidth] = useState(420);
  const isDraggingRef  = useRef(null); // 'left' | 'right' | null
  const startXRef      = useRef(0);
  const startWidthRef  = useRef(0);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDraggingRef.current) return;
      const delta = e.clientX - startXRef.current;
      if (isDraggingRef.current === 'left') {
        setLeftWidth(w => Math.max(180, Math.min(560, startWidthRef.current + delta)));
      } else {
        setRightWidth(w => Math.max(200, Math.min(640, startWidthRef.current - delta)));
      }
    };
    const onMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = null;
        document.body.classList.remove('panel-resizing');
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup',   onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup',   onMouseUp);
    };
  }, []);

  const handleSetAutoStop = (enabled) => {
    setAutoStop(enabled);
  };

  const handleSetLiveExport = (enabled) => {
    setLiveExport(enabled);
  };

  const checkMarketStatus = useCallback(async () => {
    try {
      const status = await getMarketStatus(); /*'Open'*/
      setMarketStatus(status);
      return status;
    } catch (err) {
      console.warn('Failed to check market status:', err);
      setMarketStatus(null);
      return null;
    }
  }, []);

  const isMarketCloseReached = useCallback(() => {
    const now = new Date();
    const stopTime = new Date();
    stopTime.setHours(15, 40, 0, 0);
    return now >= stopTime;
  }, []);

  const fetchAnalysis = useCallback(async () => {
    // ── Determine if market is genuinely closed ───────────────────────────
    const ist = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const istMins = ist.getHours() * 60 + ist.getMinutes();
    const afterActualClose = istMins >= 15 * 60 + 40;
    const beforeMarketOpen = istMins < 9 * 60 + 15;
    const nseApiSaysClose  = marketStatus && marketStatus.toLowerCase().startsWith('close');
    // Ignore NSE "Close" string between 15:30–15:40 — their API is stale during the extended window
    const marketGenuinelyClosed =
      (autoStop && isMarketCloseReached()) ||
      (nseApiSaysClose && (afterActualClose || beforeMarketOpen));

    if (marketGenuinelyClosed) {
      // Do one final fetch to capture the last moment's data, then stop
      if (finalFetchDoneRef.current) {
        setIsRunning(false);
        return;
      }
      finalFetchDoneRef.current = true;
      // Fall through to the fetch below — after it completes we stamp the snapshot time
    }

    try {
      console.log('Fetching analysis with config:', config);
      const LEAD_STOCK_INDICES = ['NIFTY', 'BANKNIFTY', 'SENSEX', 'BANKEX'];
      const strike = (activeConfigRef.current.strikePrice || config.strikePrice || 0).toString();
      const expiry = activeConfigRef.current.expiryDate;

      const [analysis, indicies, gamma, ema, leadStocks] = await Promise.all([
        config.kiteMode
          ? getKiteAnalysis(config.symbol, expiry, strike, currentUser?.username)
          : getAnalysis(config.symbol, expiry, strike, config.optionMode),
        getAllIndicies(config.symbol, config.optionMode),
        config.kiteMode
          ? getKiteGammaAnalysis(config.symbol, expiry, currentUser?.username).catch(() => null)
          : getGammaAnalysis(config.symbol, expiry, config.optionMode),
        getEmaData(config.symbol).catch(() => null),
        LEAD_STOCK_INDICES.includes(config.symbol)
          ? getLeadingStocks(config.symbol, expiry, config.leadStocks).catch(() => null)
          : Promise.resolve(null),
      ]);

      // ── marketSnapshot and lead stocks: SSE doesn't carry these — always apply ──
      setMarketSnapshot(indicies);
      if (leadStocks) setLeadingStocksData(leadStocks);

      // VIX direction — derived from indicies; always apply regardless of SSE
      if (indicies?.vix != null) {
        const prevVix = prevVixRef.current;
        if (prevVix != null)
          setVixDirection(indicies.vix < prevVix ? 'declining' : indicies.vix > prevVix ? 'rising' : 'flat');
        prevVixRef.current = indicies.vix;
      }

      // ── Skip analysis/gamma/ema if SSE is delivering them live ───────────
      // The SSE handler applies these + all derived state (ATM, S/R, OI matrix,
      // gamma flip, PCR) on every 5-second push. Applying stale HTTP results on
      // top of fresh SSE data causes the "random order" oscillation the user sees.
      const sseAlive = sseHasDeliveredRef.current && !finalFetchDoneRef.current;

      // 3. Expiry rollover — always (independent of SSE)
      if (!manualExpiryRef.current) {
        const MONTHS = { Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11 };
        const parsedExpiry = (() => {
          try {
            const [d, m, y] = activeConfigRef.current.expiryDate.split('-');
            return new Date(parseInt(y), MONTHS[m], parseInt(d));
          } catch { return null; }
        })();
        const todayMidnight = new Date(); todayMidnight.setHours(0, 0, 0, 0);
        if (parsedExpiry && todayMidnight > parsedExpiry) {
          getExpiryDates(config.symbol, config.optionMode).then(dates => {
            if (dates && dates.length > 0) {
              activeConfigRef.current.expiryDate = dates[0];
              setActiveExpiryDate(dates[0]);
              setAvailableExpiries(dates);
            }
          }).catch(() => {});
        }
      }

      if (sseAlive) {
        // SSE is the live source — analysis/gamma/ema already applied in SSE handler.
        setError(null);
        return;
      }

      // ── SSE not yet active: apply everything from HTTP response ─────────
      setCurrentAnalysis(analysis);
      setGammaData(gamma);
      setEmaData(ema);

      // ATM strike + S/R
      if (!manualStrike && gamma?.atmStrike && gamma.atmStrike !== activeConfigRef.current.strikePrice)
        activeConfigRef.current.strikePrice = gamma.atmStrike;
      if (gamma?.callWallStrike) setAutoResistance(gamma.callWallStrike);
      if (gamma?.putWallStrike)  setAutoSupport(gamma.putWallStrike);

      // PCR history
      if (analysis?.pcr != null)
        setPcrHistory(prev => [...prev.slice(-9), analysis.pcr]);

      // OI matrix history
      if (analysis?.ceOIMatrix != null || analysis?.peOIMatrix != null)
        setOiMatrixHistory(prev => [...prev.slice(-2),
          { ce: analysis.ceOIMatrix ?? null, pe: analysis.peOIMatrix ?? null }]);

      // Gamma flip crossover
      const spot = gamma?.spotPrice;
      const flip = gamma?.gammaFlipLevel;
      const prevSpot = prevGammaSpotRef.current;
      if (spot != null && flip != null && prevSpot != null) {
        if (prevSpot <= flip && spot > flip) {
          setGammaFlipCrossover('UP'); gammaFlipCrossAgeRef.current = 0;
        } else if (prevSpot >= flip && spot < flip) {
          setGammaFlipCrossover('DOWN'); gammaFlipCrossAgeRef.current = 0;
        } else {
          gammaFlipCrossAgeRef.current++; if (gammaFlipCrossAgeRef.current > 6) setGammaFlipCrossover(null);
        }
      }
      if (spot != null) prevGammaSpotRef.current = spot;

      setAnalysisData((prev) => [analysis, ...prev].slice(0, 500));
      setError(null);

      // Auto-export to CSV if live export is enabled
      if (liveExport) {
        const updatedData = [analysis, ...analysisData.slice(0, 499)];
        const csv = [
          ['Time', 'Value', 'Call Sum', 'Put Sum', 'Difference', 'Call Boundary', 'Put Boundary', 'Call ITM', 'Put ITM'],
          ...updatedData.map((row) => [
            row.time,
            row.value,
            row.callSum,
            row.putSum,
            row.difference,
            row.callBoundary,
            row.putBoundary,
            row.callITMTable,
            row.putITMTable,
          ]),
        ]
          .map((row) => row.join(','))
          .join('\n');

        const element = document.createElement('a');
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
        element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(csv));
        element.setAttribute('download', `Option-Chain-Analyzer-${config.symbol}-${config.expiryDate}-${timestamp}.csv`);
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
        console.log('Live export: CSV downloaded automatically');
      }
      // If this was the final close-snapshot fetch, stamp the time and stop
      if (finalFetchDoneRef.current) {
        const snapTime = new Date().toLocaleTimeString('en-IN', {
          hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
        });
        setClosedSnapshotTime(snapTime);
        setIsRunning(false);
      }
    } catch (err) {
      console.error('Error fetching analysis:', err);
      setError('Failed to fetch analysis data');
      if (finalFetchDoneRef.current) setIsRunning(false);
    }
  }, [config, autoStop, isMarketCloseReached, marketStatus, liveExport, analysisData]);

  // Pre-populate panels from backend snapshot on page load.
  // The backend's Kite fast-poll (5 s) or NSE scheduler keeps the snapshot fresh
  // even when the frontend is closed — so the dashboard is instant, not blank.
  useEffect(() => {
    if (!config?.symbol) return;
    getSnapshot(config.symbol).then((snap) => {
      if (!snap || !snap.hasData) return;
      if (snap.gamma)    setGammaData(snap.gamma);
      if (snap.analysis) setAnalysisData((prev) => prev.length === 0 ? [snap.analysis] : prev);
    }).catch(() => {});
  }, [config?.symbol]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isRunning || !currentUser?.username) return;

    // ── SSE primary path ──────────────────────────────────────────────────────
    // The backend pushes AnalysisData + GammaAnalysis + EmaData on every compute
    // cycle (5 s on Kite stream, ~60 s on NSE scheduler). No polling needed.
    let sseCleanup = null;
    let fallbackInterval = null;
    let sseReceivedData = false;

    const startSse = () => {
      sseCleanup = subscribeToStream(
        currentUser.username,
        config?.symbol,
        (payload) => {
          // Only process data for the selected symbol
          if (payload.symbol && config?.symbol && payload.symbol !== config.symbol) return;
          sseReceivedData = true;
          sseHasDeliveredRef.current = true;
          setSseConnected(true);
          // Stop polling fallback once SSE is delivering data
          if (fallbackInterval) { clearInterval(fallbackInterval); fallbackInterval = null; }

          if (payload.gamma) {
            setGammaData(payload.gamma);
            // ATM strike auto-track (SSE is now primary — keep activeConfigRef current)
            if (!manualStrikeRef.current && payload.gamma.atmStrike &&
                payload.gamma.atmStrike !== activeConfigRef.current.strikePrice) {
              activeConfigRef.current.strikePrice = payload.gamma.atmStrike;
            }
            // S/R from OI walls
            if (payload.gamma.callWallStrike) setAutoResistance(payload.gamma.callWallStrike);
            if (payload.gamma.putWallStrike)  setAutoSupport(payload.gamma.putWallStrike);
            // Gamma flip crossover detection
            const spot = payload.gamma.spotPrice;
            const flip = payload.gamma.gammaFlipLevel;
            const prevSpot = prevGammaSpotRef.current;
            if (spot != null && flip != null && prevSpot != null) {
              if (prevSpot <= flip && spot > flip) {
                setGammaFlipCrossover('UP');
                gammaFlipCrossAgeRef.current = 0;
              } else if (prevSpot >= flip && spot < flip) {
                setGammaFlipCrossover('DOWN');
                gammaFlipCrossAgeRef.current = 0;
              } else {
                gammaFlipCrossAgeRef.current += 1;
                if (gammaFlipCrossAgeRef.current > 6) setGammaFlipCrossover(null);
              }
            }
            if (spot != null) prevGammaSpotRef.current = spot;
          }
          if (payload.ema) setEmaData(payload.ema);
          if (payload.verdict) setBackendVerdict(payload.verdict);

          // This user's own open manual trades on this symbol, per ManualTradeTrackingService.
          // Detect any clientId that dropped out since the last update (backend auto-closed it,
          // or it was squared off from another tab) and refresh tradeHistory so the Win Rate
          // Tracker / GreekStopLossPanel see the final outcome without a page reload.
          const nowPositions = payload.manualPositions ?? [];
          const nowIds = new Set(nowPositions.map(p => Number(p.clientId)));
          let anyClosed = false;
          seenManualPositionIdsRef.current.forEach(id => { if (!nowIds.has(id)) anyClosed = true; });
          seenManualPositionIdsRef.current = nowIds;
          setManualPositions(nowPositions);
          if (anyClosed) refreshTradeHistory();
          if (payload.analysis) {
            setCurrentAnalysis(payload.analysis);
            setAnalysisData((prev) => [payload.analysis, ...prev].slice(0, 500));
            if (payload.analysis.pcr != null)
              setPcrHistory((prev) => [...prev.slice(-9), payload.analysis.pcr]);
            if (payload.analysis.ceOIMatrix != null || payload.analysis.peOIMatrix != null)
              setOiMatrixHistory(prev => [...prev.slice(-2),
                { ce: payload.analysis.ceOIMatrix ?? null, pe: payload.analysis.peOIMatrix ?? null }]);
          }
        },
        (_err) => {
          // SSE connection error — EventSource will auto-reconnect.
          // If no data received yet, start polling fallback so users see data immediately.
          setSseConnected(false);
          if (!sseReceivedData && !fallbackInterval) {
            fetchAnalysis();
            fallbackInterval = setInterval(fetchAnalysis, liveRefreshInterval);
          }
        },
        (ticks) => { if (!ticks?.symbol || !config?.symbol || ticks.symbol === config.symbol) setLiveTicks(ticks); }
      );
    };

    // ── Polling fallback (runs until SSE delivers first update) ───────────────
    // If the backend has no Kite stream active, SSE events may be infrequent
    // (only on NSE scheduler ticks). Fallback polling keeps the UI responsive.
    fetchAnalysis();
    fallbackInterval = setInterval(fetchAnalysis, liveRefreshInterval);

    // Start SSE — when it delivers data it cancels the fallback interval above.
    startSse();

    return () => {
      if (sseCleanup)       sseCleanup();
      if (fallbackInterval) clearInterval(fallbackInterval);
      setSseConnected(false);
      sseHasDeliveredRef.current = false; // reset so new symbol starts clean
      seenManualPositionIdsRef.current = new Set();
      setManualPositions([]);
    };
  }, [isRunning, marketStatus, liveRefreshInterval, currentUser?.username, config?.symbol]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isRunning || !autoStop) return;

    const timeout = setInterval(async () => {
      if (isMarketCloseReached()) {
        console.log('Market close time reached (15:40) — triggering final snapshot fetch');
        // fetchAnalysis will detect marketGenuinelyClosed, do the final fetch, then stop
        if (!finalFetchDoneRef.current) fetchAnalysis();
        else setIsRunning(false);
      } else {
        try {
            const status = await checkMarketStatus();
            if (status && status.toLowerCase().startsWith('close')) {
                const ist = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
                const istMins = ist.getHours() * 60 + ist.getMinutes();
                const afterActualClose = istMins >= 15 * 60 + 40;
                const beforeMarketOpen = istMins < 9 * 60 + 15;
                if (afterActualClose || beforeMarketOpen) {
                    if (!finalFetchDoneRef.current) fetchAnalysis();
                    else setIsRunning(false);
                }
            }
        } catch(err) {
            console.error('Error checking market status: ', err);
        }
      }

    }, 180000);

    return () => clearInterval(timeout);
  }, [isRunning, autoStop, isMarketCloseReached, checkMarketStatus]);

  useEffect(() => {
    // Check market status on component mount and periodically
    checkMarketStatus();
    const statusInterval = setInterval(checkMarketStatus, 180000); // Check every 5 minute
    return () => clearInterval(statusInterval);
  }, [checkMarketStatus]);

  const handleStart = () => {
    finalFetchDoneRef.current = false;
    setClosedSnapshotTime(null);
    setIsRunning(true);
    fetchAnalysis();
    triggerAutoStart().catch(err =>
      console.warn('Auto-start notification to backend failed:', err)
    );
    if (config.kiteMode) {
      // Kite mode's expiry is fixed to whatever was subscribed on the shared WS
      // connection — NSE's expiry list is wrong here (NSE has no BSE/SENSEX data
      // at all, and even for NFO symbols it won't include a manually-entered date),
      // and switching to an unsubscribed expiry wouldn't work against the backend
      // anyway. Show only the one expiry actually in use.
      setAvailableExpiries([activeConfigRef.current.expiryDate]);
    } else {
      // Fetch expiry list for runtime switching
      getExpiryDates(config.symbol, config.optionMode)
        .then(dates => { if (dates?.length) setAvailableExpiries(dates); })
        .catch(() => {});
    }
  };

  const handleStop = () => {
    setIsRunning(false);
  };

  // Defense-in-depth: the MenuBar dropdowns are already disabled while hasLiveTrade is true,
  // but these guards make the handlers themselves no-ops too, so no path (disabled-select
  // edge case, future caller) can change the polled expiry/strike out from under an open
  // trade's LTP lookup — that's exactly what corrupted a live trade's P&L calculation before.
  const handleExpiryChange = (expiry) => {
    if (hasLiveTrade) return;
    activeConfigRef.current.expiryDate = expiry;
    setActiveExpiryDate(expiry);
    manualExpiryRef.current = true; // stop auto-rollover from overriding this
    if (isRunning) fetchAnalysis();  // immediate refresh with new expiry
  };

  const handleStrikeChange = (strike) => {
    if (hasLiveTrade) return;
    const val = strike === '' ? null : Number(strike);
    setManualStrike(val);
    if (val !== null) activeConfigRef.current.strikePrice = val;
  };

  const handleExport = () => {
    const csv = [
      ['Time', 'Value', 'Call Sum', 'Put Sum', 'Difference', 'Call Boundary', 'Put Boundary', 'Call ITM', 'Put ITM'],
      ...analysisData.map((row) => [
        row.time,
        row.value,
        row.callSum,
        row.putSum,
        row.difference,
        row.callBoundary,
        row.putBoundary,
        row.callITMTable,
        row.putITMTable,
      ]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(csv));
    element.setAttribute('download', `Option-Chain-Analyzer-${config.symbol}-${config.expiryDate}.csv`);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  useEffect(() => {
    if(currentAnalysis) {
        const putSum = currentAnalysis.putSum || 0;
        const callSum = currentAnalysis.callSum || 0;
        const pcr = callSum > 0 ? (putSum / callSum).toFixed(2) : 0;
        document.title = `PCR: ${pcr} - BSG Traders`;
    } else {
        document.title = 'BSG Traders';
    }
  }, [currentAnalysis]);

  return (
    <div className="analyzer-page">
      <MenuBar
        config={config}
        isRunning={isRunning}
        onStart={handleStart}
        onStop={handleStop}
        onExport={handleExport}
        onLogout={onLogout}
        autoStop={autoStop}
        onSetAutoStop={handleSetAutoStop}
        liveExport={liveExport}
        onSetLiveExport={handleSetLiveExport}
        notificationsEnabled={notificationsEnabled}
        onNotificationsChange={setNotificationsEnabled}
        refreshInterval={liveRefreshInterval}
        onIntervalChange={setLiveRefreshInterval}
        availableExpiries={availableExpiries}
        activeExpiry={activeExpiryDate}
        onExpiryChange={handleExpiryChange}
        availableStrikes={gammaData?.strikeData?.map(s => s.strikePrice) ?? []}
        activeStrike={manualStrike ?? activeConfigRef.current.strikePrice}
        onStrikeChange={handleStrikeChange}
        liveTradeLock={hasLiveTrade}
        liveLotSize={liveLotSize}
        onLotSizeChange={setLiveLotSize}
        currentUser={currentUser}
        onManageUsers={() => setShowUserMgmt(true)}
        onViewTrades={() => setShowAdminTrades(true)}
        onViewPerformance={() => setShowPerformance(true)}
        onManageRiskSettings={() => setShowRiskSettings(true)}
        viewerTelegramId={isViewer ? (currentUser?.telegramChatId ?? null) : null}
      />

      {showUserMgmt && isAdmin && (
        <UserManagementPanel
          currentUser={currentUser}
          onClose={() => setShowUserMgmt(false)}
        />
      )}

      {showAdminTrades && isAdmin && (
        <AdminTradesDashboard
          currentUser={currentUser}
          onClose={() => setShowAdminTrades(false)}
        />
      )}

      {showPerformance && currentUser && (
        <PerformancePanel
          currentUser={currentUser}
          onClose={() => setShowPerformance(false)}
        />
      )}

      {showRiskSettings && (
        <RiskSettingsPanel
          currentUser={currentUser}
          sellerEnabledNow={backendSystemStatus?.sellerEnabled}
          sellerSwitchLocked={backendSystemStatus?.sellerSwitchLocked === true}
          dailyLimitsNow={backendSystemStatus?.dailyLimits}
          sizingNow={backendSystemStatus?.sizing}
          onClose={() => setShowRiskSettings(false)}
          onSaved={(updated) => {
            onUpdateUser?.(updated);
            setShowRiskSettings(false);
          }}
        />
      )}

      <div className="analyzer-content">
        <div className="analyzer-header">
          <div className="brand-header">
            <img src={logo} alt="BSG Traders" className="brand-logo" />
            <h1>BSG Traders - v1.0</h1>
            {config.kiteMode && onBack && (
              <button
                className="kite-back-btn"
                onClick={onBack}
                title="Back to symbol picker — the Kite stream keeps running"
              >
                ← Symbols
              </button>
            )}
          </div>
          <p>
            {config.optionMode}: {config.symbol}
            {['SENSEX', 'BANKEX'].includes(config.symbol) ? ' (BSE)' : ' (NSE)'}
            {' '} | Expiry: {activeExpiryDate}
            {gammaData?.activeExpiry && gammaData.activeExpiry !== activeExpiryDate && (
              <span className="expiry-mismatch-badge"
                title="The backend's autonomous engine (including the self-adjusting theta-safe picker) is computing this data for a different expiry than the one selected above — prices/greeks shown reflect ITS expiry, not yours.">
                {' '}⚠️ Backend tracking: {gammaData.activeExpiry}
              </span>
            )}
            {gammaData?.sellerExpiry && gammaData.sellerExpiry !== (gammaData.activeExpiry ?? activeExpiryDate) && (
              <span title="The range-day option seller trades the nearest non-expiring contract; the buyer and this dashboard's data use the expiry above.">
                {' '}| 🧊 Seller: {gammaData.sellerExpiry}
              </span>
            )}
            {' '} | ATM: {(gammaData?.atmStrike ?? activeConfigRef.current.strikePrice) || '—'}
            {autoSupport    ? ` | S: ${autoSupport}`    : ''}
            {autoResistance ? ` | R: ${autoResistance}` : ''}
            {config.autoMode && <span className="auto-mode-badge" title="ATM + S/R auto-managed from live OI data"> AUTO</span>}
          </p>
          {expiryAutoSyncedFrom && expiryAutoSyncedFrom !== activeExpiryDate && (
            <div className="expiry-autosync-banner">
              ⚠️ <b>Theta-Decay Auto-Adjustment:</b> the backend's autonomous engine switched{' '}
              {config.symbol} to expiry <b>{activeExpiryDate}</b> because the near-week contract's
              theta decay exceeded the safety cap — this dashboard was tracking{' '}
              <b>{expiryAutoSyncedFrom}</b> and has been synced to match what the SYSTEM is
              actually trading.
              <button
                className="expiry-autosync-dismiss"
                onClick={() => setExpiryAutoSyncedFrom(null)}
                title="Dismiss"
              >
                ✕
              </button>
            </div>
          )}
          <p>Status: {isRunning ? '🟢 Running' : '🔴 Stopped'}</p>
          {isRunning && (
            <p>Feed: {sseConnected ? '⚡ Live (SSE)' : '🔄 Polling'}</p>
          )}
          {marketStatus && (
            <p>Market: {(() => {
              const isClosed = marketStatus.toLowerCase().startsWith('close');
              if (!isClosed) return '🟢 Open';
              // NSE API may report "Close" between 15:30–15:40; show Open until actual close
              const ist = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
              const istMins = ist.getHours() * 60 + ist.getMinutes();
              if (istMins >= 9 * 60 + 15 && istMins < 15 * 60 + 40) return '🟢 Open';
              return '🔴 Closed';
            })()}</p>
          )}
        </div>

        {/* ── Market closed snapshot banner ──────────────────────────────── */}
        {closedSnapshotTime && !isRunning && (
          <div className="market-closed-banner">
            🔴 Market Closed — showing last data captured at {closedSnapshotTime}.
            All panels reflect the final state at close.
          </div>
        )}

        {error && <div className="error-banner">{error}</div>}

        {!notificationsEnabled && (
          <div className="notif-disabled-banner">
            <span className="notif-disabled-icon">🔕</span>
            Telegram notification has been stop due to volatility in the market by the author.
          </div>
        )}

        {marketStatus && marketStatus.toLowerCase().startsWith('close') && (() => {
          // NSE API often returns "Close" from ~3:30 PM — ignore it until the actual
          // extended close (3:40 PM IST). Only show the banner outside trading hours.
          const ist     = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
          const istMins = ist.getHours() * 60 + ist.getMinutes();
          const duringSession = istMins >= 9 * 60 + 15 && istMins < 15 * 60 + 40;
          return !duringSession;
        })() && (
          <div className="market-closed-banner">
            <h3>🕒 Market is Closed</h3>
            <p>The stock market is currently closed. Analysis will resume when the market opens.</p>
            <p>Market Status: {marketStatus}</p>
          </div>
        )}

        {(currentAnalysis || analysisData.length > 0) ? (
          <div className={`analyzer-panels${isViewer ? ' viewer-panels' : ''}`}>
            <div className="panel-left" style={isViewer ? undefined : { width: leftWidth }}>
              {currentAnalysis && gammaData && (
                <TradingSetupPanel
                  analysis={currentAnalysis}
                  gammaData={gammaData}
                  emaData={emaData}
                  vixDirection={vixDirection}
                  maxPainPrice={currentAnalysis?.maxPainPrice}
                  pcrHistory={pcrHistory}
                  support={autoSupport || config.support || undefined}
                  resistance={autoResistance || config.resistance || undefined}
                  symbol={config.symbol}
                  expiryDate={activeExpiryDate}
                  configStrikePrice={manualStrike ?? activeConfigRef.current.strikePrice}
                  liveLotSize={liveLotSize}
                  sessionTrades={sessionTrades}
                  onMarkWin={() => setSessionTrades(prev => ({ ...prev, wins: prev.wins + 1 }))}
                  onMarkLoss={() => setSessionTrades(prev => ({ ...prev, losses: prev.losses + 1 }))}
                  onResetSession={() => setSessionTrades({ wins: 0, losses: 0 })}
                  onRecordSignal={handleRecordSignal}
                  onSquareOff={handleSquareOff}
                  onVerdictChange={setLiveVerdict}
                  onCircuitBreaker={handleCircuitBreaker}
                  tradeHistory={tradeHistory}
                  oiMatrixHistory={oiMatrixHistory}
                  gammaFlipCrossover={gammaFlipCrossover}
                  leadingStocksData={leadingStocksData}
                  backendVerdict={backendVerdict}
                  onRiskLots={onRiskLots}
                  takeTradeLots={takeTradeLots}
                  backendSystemStatus={backendSystemStatus}
                  onLiveTradeChange={setLiveTrade}
                />
              )}
              <FuturesPanel gammaData={gammaData} />
              {!isViewer && <WeeklyCondorPanel username={currentUser?.username} symbol={config.symbol} />}
              {!isViewer && <LevelShortPanel username={currentUser?.username} symbol={config.symbol} />}
              <TradeHistoryPanel
                trades={tradeHistory}
                gammaData={gammaData}
                onMarkOutcome={handleMarkOutcome}
                onSquareOff={handleSquareOff}
                onClearHistory={handleClearHistory}
                onGenerateReport={generateEODReport}
                onGenerateWeeklyReport={generateWeeklyReport}
                onGenerateMonthlyReport={generateMonthlyReport}
                openStrategy={backendSystemStatus?.openStrategy}
              />
              <EmaPanel emaData={emaData} />
              <GreekStopLossPanel
                gammaData={gammaData}
                symbol={config.symbol}
                expiryDate={activeExpiryDate}
                onRecordSignal={handleRecordSignal}
                onSquareOff={handleSquareOff}
                defaultLots={takeTradeLots}
                systemVerdict={liveVerdict}
                manualPositions={manualPositions}
                tradeHistory={tradeHistory}
              />
              {currentAnalysis && (
                <IndicatorPanel
                  analysis={currentAnalysis}
                  optionMode={config.optionMode}
                  marketSnapshot={marketSnapshot}
                  symbol={config.symbol}
                  support={autoSupport || config.support || undefined}
                  resistance={autoResistance || config.resistance || undefined}
                />
              )}
            </div>

            {!isViewer && (
              <div
                className="panel-resize-handle"
                onMouseDown={(e) => {
                  isDraggingRef.current = 'left';
                  startXRef.current     = e.clientX;
                  startWidthRef.current = leftWidth;
                  document.body.classList.add('panel-resizing');
                  e.preventDefault();
                }}
              />
            )}

            {!isViewer && (
              <div className="panel-center">
                {gammaData && <GammaPanel gammaData={gammaData} gammaFlipCrossover={gammaFlipCrossover} />}
                {gammaData && <PremiumBehaviourPanel gammaData={gammaData} />}
                {gammaData && <VolumeSpikePanel gammaData={gammaData} />}
              </div>
            )}

            {!isViewer && (
              <div
                className="panel-resize-handle"
                onMouseDown={(e) => {
                  isDraggingRef.current = 'right';
                  startXRef.current     = e.clientX;
                  startWidthRef.current = rightWidth;
                  document.body.classList.add('panel-resizing');
                  e.preventDefault();
                }}
              />
            )}

            <div className="panel-right" style={isViewer ? undefined : { width: rightWidth }}>
              {analysisData.length > 0 && <DataTable data={analysisData} />}
            </div>
          </div>
        ) : (
          !isRunning && (
            <div className="start-prompt">
              <p>Click "Start" from the menu to begin analysis</p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default AnalyzerPage;
